<template>
  <div class="force_graph_container">
    <div class="level_controls">
      <div class="level_label">Level</div>
      <div v-if="showlevel" class="level_value">
        <i class="iconfont icon-lujing" @click="showlevel = false" />{{
          currentLevel
        }}
      </div>
      <div v-else class="level_value active">
        <i class="iconfont icon-lujing" @click="showlevel = true" />
        <button
          @click="showLevel(item)"
          v-for="item in maxLevel"
          :key="item.index"
          class="btn_control"
          :class="{ active: item == currentLevel }"
        >
          {{ item }}
        </button>
      </div>
    </div>
    <div class="graph_controls">
      <button @click="fullScreen" v-if="!isFullScreen" class="btn_control">
        <i class="iconfont icon-quanping" />
      </button>
      <button @click="exitFullScreen" v-else class="btn_control">
        <i class="iconfont icon-suohui" />
      </button>
      <button @click="zoomLarge" class="btn_control">
        <i class="iconfont icon-fangda" />
      </button>
      <button @click="zoomSmall" class="btn_control">
        <i class="iconfont icon-suoxiao" />
      </button>
      <button @click="resetZoom" class="btn_control">
        <i class="iconfont icon-huanyuan" />
      </button>
    </div>
    <svg ref="svgContainer" class="force-graph-svg"></svg>
    <div class="search_tips_box" v-show="ishowSearchDetail">
      <div class="tips_box_header">
        <div class="close_btn" @click="ishowSearchDetail = false">
          <i class="iconfont icon-cuowu"></i>
        </div>
      </div>

      <div class="tips_box_content">
        <div
          class="tips_content_list"
          v-for="search in relationshipSearch"
          :key="search.label"
        >
          <div class="tips_content_list_label">{{ search.label }}</div>
          <div class="tips_content_list_value">
            {{ search.value }}
          </div>
        </div>
      </div>
    </div>
    <div class="d3_tips" :style="tooltipStyle" v-show="ishowTooltip">
      <div class="d3_tips_name">{{ selectedNode?.name }}</div>
      <div class="tips_content">
        <div class="tips_content_item">
          <img :src="selectedNode?.countryImg" />
        </div>
        <div class="tips_content_item status_btn">
          {{ selectedNode?.entStatus }}
        </div>
        <div class="tips_content_item">{{ selectedNode?.entType || "-" }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3";
import { mixins1 } from "@/mixins/index";

export default {
  name: "RelationshipD3",
  mixins: [mixins1],
  data() {
    return {
      simulation: null,
      svg: null,
      g: null,
      zoom: null,
      currentZoom: 1,
      width: 0,
      height: 0,
      nodes: [],
      links: [],
      linkObjects: [],
      activeNodeId: null,
      isFullScreen: false,
      hasCentered: false,
      maxLevel: 1,
      currentLevel: 1, // 当前显示的层级
      showlevel: true,
      ishowSearchDetail: false,
      ishowTooltip: false,
      selectedNode: {},
      tooltipStyle: {
        left: "0px",
        top: "0px",
      },
    };
  },
  props: {
    chartData: {
      type: Object,
      default: () => ({
        nodes: [],
        links: [],
      }),
    },
    relationshipSearch: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    chartData: {
      handler(val, oldV) {
        this.initGraph();
      },
      deep: true,
    },
  },
  mounted() {
    if (this.chartData) {
      this.initGraph();
    }
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.simulation) {
      this.simulation.stop();
    }
  },
  methods: {
    initGraph() {
      const container = this.$refs.svgContainer;
      if (!container) return;

      this.width = container.parentElement.clientWidth || 800;
      this.height = container.parentElement.clientHeight || 600;

      // 清空之前的内容
      d3.select(this.$refs.svgContainer).selectAll("*").remove();

      this.svg = d3
        .select(this.$refs.svgContainer)
        .attr("width", this.width)
        .attr("height", this.height);

      // this.svg.call(this.zoom);
      // 创建主群组
      this.g = this.svg
        .append("g")
        .attr("transform", `translate(${this.width / 2}, ${this.height / 2})`);

      // 添加缩放功能
      this.zoom = d3.zoom().on("zoom", (event) => {
        this.g.attr("transform", event.transform);
        // keep track of current zoom scale so centering calculations use it
        this.currentZoom = event.transform.k;
      });
      // 绑定 zoom
      this.svg.call(this.zoom);
      // 同步 zoom 内部 transform
      this.svg.call(
        this.zoom.transform,
        d3.zoomIdentity.translate(this.width / 2, this.height / 2),
      );
      // 准备数据
      this.prepareData();
      // 计算每个节点层级
      this.computeLevels();
      // 创建力模拟
      this.createForceSimulation();

      // 绘制图形
      this.drawGraph();

      // 初始化时显示当前设置的层级（默认 showLevel(2) 会高亮第1、2层）
      this.showLevel(this.currentLevel);
    },

    prepareData() {
      // 使用传入的数据或使用默认数据
      this.nodes =
        this.chartData.nodes && this.chartData.nodes.length > 0
          ? JSON.parse(JSON.stringify(this.chartData.nodes))
          : [];

      this.links =
        this.chartData.links && this.chartData.links.length > 0
          ? JSON.parse(JSON.stringify(this.chartData.links))
          : [];
    },
    // 创建力模拟
    createForceSimulation() {
      // 转换链接ID为对象引用
      this.linkObjects = this.links
        .map((d) => {
          const source = this.nodes.find((n) => n.id == d.source);
          const target = this.nodes.find((n) => n.id == d.target);
          return source && target ? { ...d, source, target } : null;
        })
        .filter(Boolean);

      // 为重复的链接计算索引（处理多条相同关系的链接）
      const linkPairs = new Map();
      this.linkObjects.forEach((link) => {
        const key1 = `${link.source.id}→${link.target.id}`;
        const key2 = `${link.target.id}→${link.source.id}`;

        // 检查两个方向的键是否已存在
        if (linkPairs.has(key1)) {
          linkPairs.get(key1).push(link);
        } else if (linkPairs.has(key2)) {
          linkPairs.get(key2).push(link);
        } else {
          linkPairs.set(key1, [link]);
        }
      });

      // 为每条链接设置索引和总数
      linkPairs.forEach((links) => {
        links.forEach((link, index) => {
          link.linkIndex = index;
          link.linksCount = links.length;
        });
      });

      this.simulation = d3
        .forceSimulation(this.nodes)
        .force(
          "link",
          d3
            .forceLink(this.linkObjects)
            .id((d) => d.id)
            .distance(120)
            .strength(0.5),
        )
        .force("charge", d3.forceManyBody().strength(-200))
        .force("center", d3.forceCenter(this.width / 2, this.height / 2))
        .force(
          "collision",
          d3.forceCollide().radius((d) => this.getNodeRadius(d) + 40),
        )
        .force(
          "radial",
          d3
            .forceRadial(
              (d) => (d.level ?? 1) * 120,
              this.width / 2,
              this.height / 2,
            )
            .strength(0.5),
        );

      //   this.simulation.alpha(1).restart();

      // 快速运行模拟到稳定状态（跳过加载动画）
      for (let i = 0; i < 300; i++) {
        this.simulation.tick();
      }
    },
    // 绘制图形
    drawGraph() {
      // 定义箭头标记
      const defs = this.g.append("defs");
      // defs
      //   .append("marker")
      //   .attr("id", "arrowhead")
      //   .attr("markerWidth", 6)
      //   .attr("markerHeight", 6)
      //   .attr("refX", 21)
      //   .attr("refY", 2)
      //   .attr("orient", "auto")
      //   .append("polygon")
      //   .attr("points", "0 0, 6 2, 0 4")
      //   .attr("fill", "#999");
      // 目标节点箭头
      defs
        .append("marker")
        .attr("id", "arrow-target")
        .attr("markerWidth", 6)
        .attr("markerHeight", 6)
        .attr("viewBox", "0 0 6 6")
        .attr("refX", 26 + 6) // ⭐ 目标节点半径 + 箭头补偿
        .attr("refY", 3)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,0 L6,3 L0,6 Z")
        .attr("fill", "#999");

      // 普通节点箭头
      defs
        .append("marker")
        .attr("id", "arrow-normal")
        .attr("markerWidth", 6)
        .attr("markerHeight", 6)
        .attr("viewBox", "0 0 6 6")
        .attr("refX", 18 + 6)
        .attr("refY", 3)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,0 L6,3 L0,6 Z")
        .attr("fill", "#999");

      // 绘制链接
      const link = this.g
        .selectAll(".link")
        .data(this.linkObjects)
        .enter()
        .append("path")
        .attr("class", "link")
        .attr("stroke", "#999")
        .attr("stroke-width", 2)
        .attr("stroke-opacity", 0.6)
        .attr("fill", "none")
        .attr("marker-end", (d) =>
          d.target.type === "target"
            ? "url(#arrow-target)"
            : "url(#arrow-normal)",
        );

      // 绘制链接标签
      const linkLabels = this.g
        .selectAll(".link-label")
        .data(this.linkObjects)
        .enter()
        .append("text")
        .attr("class", "link-label")
        .attr("text-anchor", "middle")
        .attr("font-size", "11px")
        .attr("fill", "#666")
        .attr("pointer-events", "none")
        .text((d) => d.relationship || "");

      // 绘制节点
      const node = this.g
        .selectAll(".node")
        .data(this.nodes)
        .enter()
        .append("circle")
        .attr("class", "node")
        .attr("r", (d) => this.getNodeRadius(d))
        .attr("fill", (d) => this.getNodeColor(d))
        .attr("stroke", "#fff")
        .attr("stroke-width", 2)
        .call(this.drag(this.simulation));

      // 添加节点标签（最多三行，超出用...）
      const self = this;
      const labels = this.g
        .selectAll(".label")
        .data(this.nodes)
        .enter()
        .append("text")
        .attr("class", "label")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-size", "12px")
        .attr("fill", "#fff")
        .attr("pointer-events", "none")
        .attr("pointer-events", "none")
        .each(function (d) {
          const lines = self.splitMixedText(d);
          const lineHeight = 1.1;
          const offset = ((lines.length - 1) * lineHeight) / 2;
          const textEl = d3.select(this);

          lines.forEach((line, i) => {
            textEl
              .append("tspan")
              .attr("x", 0) // ⚠️ 这里只是占位
              .attr("dy", i === 0 ? `${-offset}em` : `${lineHeight}em`)
              .text(line);
          });
        });

      // 添加交互效果
      node
        .on("mouseenter", (event, d) => {
          if (event.target.style.opacity != 1) return; // 只有选中的节点才响应 hover
          this.showTooltips(event, d);
        })
        .on("mouseleave", (event, d) => {
          if (event.target.style.opacity != 1) return; // 只有选中的节点才响应 hover
          this.ishowTooltip = false;
        })
        .on("click", (event, d) => {
          event.stopPropagation(); // 防止点到 svg 空白
          // 再次点击同一个节点 → 取消选中
          if (this.activeNodeId === d.id) {
            this.activeNodeId = null;
            this.resetHighlight();
          } else {
            this.activeNodeId = d.id;
            this.highlightNode(d);
            // center view on selected node
            // this.centerOnNode(d);
          }
        });
      // 点击空白区域取消选中
      this.svg.on("click", () => {
        this.activeNodeId = null;
        this.resetHighlight();
      });

      // 更新位置
      this.simulation.on("tick", () => {
        link.attr("d", (d) => this.getPathData(d));

        node.attr("cx", (d) => d.x).attr("cy", (d) => d.y);

        labels
          .attr("x", (d) => d.x)
          .attr("y", (d) => d.y)
          .selectAll("tspan")
          .attr("x", (d) => d.x)
          .append("title");

        // 更新链接标签位置（在链接中点）
        linkLabels
          .attr("x", (d) => (d.source.x + d.target.x) / 2)
          .attr("y", (d) => this.getLabelPos(d));
      });

      // 模拟结束后重置视图
      this.simulation.on("end", () => {
        if (this.hasCentered) return;
        // 获取目标节点
        const targetNode = this.nodes.find((d) => d.type === "target");
        if (targetNode) {
          // 计算偏移量并移到中心
          const offsetX = -targetNode.x;
          const offsetY = -targetNode.y;
          this.svg
            .transition()
            .duration(300)
            .call(
              this.zoom.transform,
              d3.zoomIdentity
                .translate(this.width / 2, this.height / 2)
                .translate(offsetX, offsetY),
            );
        }
      });
    },
    // 高亮关联节点
    highlightNode(d, iscenter = false) {
      // 获取与当前节点相关的链接
      const relatedLinks = this.linkObjects.filter(
        (l) => l.source.id === d.id || l.target.id === d.id,
      );

      // 获取关联节点的ID
      const relatedNodeIds = new Set();
      relatedLinks.forEach((l) => {
        if (l.source.id === d.id) relatedNodeIds.add(l.target.id);
        if (l.target.id === d.id) relatedNodeIds.add(l.source.id);
      });
      relatedNodeIds.add(d.id); // 包含当前节点

      // 高亮关联节点
      this.g
        .selectAll(".node")
        .style("opacity", (n) => (relatedNodeIds.has(n.id) ? 1 : 0.05));

      // 高亮关联链接
      this.g.selectAll(".link").style("opacity", (l) => {
        return l.source.id === d.id || l.target.id === d.id ? 1 : 0.05;
      });

      // 高亮关联链接标签
      this.g
        .selectAll(".link-label")
        .style("opacity", (l) => {
          return l.source.id === d.id || l.target.id === d.id ? 1 : 0.05;
        })
        .style("font-weight", (l) => {
          return l.source.id === d.id || l.target.id === d.id
            ? "bold"
            : "normal";
        });

      // 高亮标签
      this.g
        .selectAll(".label")
        .style("opacity", (n) =>
          relatedNodeIds.has(n.id) || n.id === d.id ? 1 : 0.05,
        );
      // after highlighting, keep the node centered as well
      if (iscenter) {
        const d3Node = this.nodes.find((n) => n.id === d.id);
        if (!d3Node || d3Node.x == null) return;
        console.log(1111, this.nodes, d3Node);
        this.currentLevel = d3Node.level
          ? d3Node.level - 1 < 1
            ? 1
            : d3Node.level - 1
          : 1;
        this.centerOnNode(d3Node);
      }
    },
    // 实现居中功能
    centerOnNode(d) {
      if (d.x == null || d.y == null) return;
      const scale = this.currentZoom || 1;
      const x = -d.x * scale + this.width / 2;
      const y = -d.y * scale + this.height / 2;
      this.svg
        .transition()
        .duration(300)
        .call(
          this.zoom.transform,
          d3.zoomIdentity.translate(x, y).scale(scale),
        );
    },

    //展示提示信息
    showTooltips(event, d) {
      this.ishowTooltip = true;
      this.selectedNode = d;
      let countryImg = this.getCountryImgUrl(d?.countryCode);
      this.selectedNode.countryImg = require("../../../static" + countryImg);
      this.tooltipStyle = {
        left: event.pageX + 12 + "px",
        top: event.pageY + 12 + "px",
      };
    },

    // 恢复默认状态
    resetHighlight() {
      this.g.selectAll(".node").style("opacity", 1).style("filter", "none");
      this.g.selectAll(".link").style("opacity", 1);
      this.g
        .selectAll(".link-label")
        .style("opacity", 1)
        .style("font-weight", "normal");

      this.g
        .selectAll(".label")
        .style("opacity", 1)
        .style("font-weight", "normal");
    },
    // 文本三行居中，超出时在末行添加省略号
    splitMixedText(node) {
      const lines = [];
      let currentLine = "";
      let count = 0;
      let text = node.name;
      let maxUnits = node.type === "target" ? 7 : 4; // 目标公司允许更多字符
      const maxLines = 3;
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const isChinese = /[\u4e00-\u9fa5]/.test(char);
        const charWidth = isChinese ? 1 : 0.5; // 英文算半个单位，中文算一个单位

        if (count + charWidth > maxUnits) {
          lines.push(currentLine);
          currentLine = char;
          count = charWidth;
          if (lines.length === maxLines) break; // 最多三行
        } else {
          currentLine += char;
          count += charWidth;
        }
      }

      if (lines.length < maxLines && currentLine) {
        lines.push(currentLine);
      }

      // 超出内容，在最后一行加省略号
      const totalChars = lines.join("").length;
      if (text.length > totalChars) {
        const last = lines[lines.length - 1];
        lines[lines.length - 1] =
          last.length > 3
            ? last.slice(0, last.length - 3) + "..."
            : last + "...";
      }

      return lines;
    },

    // 获取节点颜色
    getNodeColor(node) {
      if (node.type == "target") {
        return "#603C2C"; // 深棕色
      } else if (node.nodeType == "Person") {
        return "#FF7600"; // 红色
      } else if (node.nodeType == "Company") {
        return "#016FF6"; // 蓝色
      } else {
        return "#999";
      }
    },
    // 展示层级
    showLevel(level) {
      this.currentLevel = level;
      let sureLevel = level + 1;

      // 获取目标节点及其直接连接的所有节点
      const targetNode = this.nodes.find((n) => n.type === "target");
      if (!targetNode) return;

      const visibleNodeIds = new Set([targetNode.id]);

      // 获取指定层级的所有节点
      this.nodes.forEach((n) => {
        if (n.level && n.level <= sureLevel) {
          visibleNodeIds.add(n.id);
        }
      });

      // 高亮当前层级及所有更小层级的节点
      this.g.selectAll(".node").style("opacity", (n) => {
        if (n.id === targetNode.id) return 1; // 目标节点始终显示
        // 检查是否在当前层级或更小的层级
        if (n.level && n.level <= sureLevel) return 1;
        return 0.05;
      });

      this.g.selectAll(".label").style("opacity", (n) => {
        if (n.id === targetNode.id) return 1;
        if (n.level && n.level <= sureLevel) return 1;
        return 0.05;
      });

      // 高亮所有在当前层级及之前层级之间的连线（两端层级均 <= level）
      this.g.selectAll(".link").style("opacity", (l) => {
        const sId = typeof l.source === "object" ? l.source.id : l.source;
        const tId = typeof l.target === "object" ? l.target.id : l.target;
        const sLevel = this.nodes.find((n) => n.id === sId)?.level;
        const tLevel = this.nodes.find((n) => n.id === tId)?.level;
        if (sLevel && tLevel && sLevel <= sureLevel && tLevel <= sureLevel) {
          return 1;
        }
        return 0.05;
      });

      this.g.selectAll(".link-label").style("opacity", (l) => {
        const sId = typeof l.source === "object" ? l.source.id : l.source;
        const tId = typeof l.target === "object" ? l.target.id : l.target;
        const sLevel = this.nodes.find((n) => n.id === sId)?.level;
        const tLevel = this.nodes.find((n) => n.id === tId)?.level;
        if (sLevel && tLevel && sLevel <= sureLevel && tLevel <= sureLevel) {
          return 1;
        }
        return 0.05;
      });
    },

    // 计算层级
    computeLevels() {
      const nodes = this.nodes;
      const links = this.links;
      const rootId = this.nodes.find((n) => n.type === "target")?.id;
      const nodeMap = new Map(nodes.map((n) => [n.id, n]));
      const adj = new Map();

      // 关键：反向建图（target -> source）
      links.forEach((l) => {
        const s = typeof l.source === "object" ? l.source.id : l.source;
        const t = typeof l.target === "object" ? l.target.id : l.target;

        if (!adj.has(t)) adj.set(t, []);
        adj.get(t).push(s);
      });

      const root = nodes.find((n) => n.type === "target");
      if (!root) {
        console.warn("未找到 type === target 的节点");
        return 0;
      }

      const queue = [{ id: root.id, level: 1 }];
      const visited = new Set([root.id]);

      root.level = 1;
      let maxLevel = 1;

      while (queue.length) {
        const { id, level } = queue.shift();
        maxLevel = Math.max(maxLevel, level);
        (adj.get(id) || []).forEach((childId) => {
          if (!visited.has(childId)) {
            visited.add(childId);
            const node = nodeMap.get(childId);
            if (!node) return;

            node.level = level + 1;
            queue.push({ id: childId, level: level + 1 });
          }
        });
      }

      this.maxLevel = maxLevel - 1; // 减1是因为根节点 level=1，实际层级数是 maxLevel-1
      // return maxLevel;
    },
    // 获取节点半径
    getNodeRadius(node) {
      return node.type === "target" ? 52 : 36;
    },

    // 拖动节点
    drag(simulation) {
      const dragstarted = (event, d) => {
        if (!event.active) simulation.alphaTarget(0.1).restart();

        // 🔒 锁住除当前节点以外的所有节点
        simulation.nodes().forEach((n) => {
          if (n !== d) {
            n.fx = n.x;
            n.fy = n.y;
          }
        });

        // 固定当前节点
        d.fx = d.x;
        d.fy = d.y;
      };

      const dragged = (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      };

      const dragended = (event, d) => {
        this.hasCentered = true;
        if (!event.active) simulation.alphaTarget(0);

        // 🔓 释放其它节点
        simulation.nodes().forEach((n) => {
          if (n !== d) {
            n.fx = null;
            n.fy = null;
          }
        });

        // 当前节点是否释放，看你的需求
        d.fx = null;
        d.fy = null;
        // 拖完后布局完全不再抖
        simulation.alpha(0);
      };

      return d3
        .drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);
    },

    // 生成贝塞尔曲线路径，用于多条链接的分离显示
    getPathData(d) {
      const x1 = d.source.x;
      const y1 = d.source.y;
      const x2 = d.target.x;
      const y2 = d.target.y;

      // 如果只有一条链接，直接使用直线
      if (d.linksCount === 1) {
        return `M${x1},${y1}L${x2},${y2}`;
      }

      // 计算直线的中点和方向向量
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;
      const dx = x2 - x1;
      const dy = y2 - y1;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // 垂直方向的单位向量（用于偏移）
      const px = -dy / dist;
      const py = dx / dist;

      // 计算偏移量（根据链接索引和总数）
      // 使链接分散开来
      const offset = (d.linkIndex - (d.linksCount - 1) / 2) * 30;

      // 控制点（用于二次贝塞尔曲线）
      const cx = mx + px * offset;
      const cy = my + py * offset;

      // 生成二次贝塞尔曲线
      return `M${x1},${y1}Q${cx},${cy} ${x2},${y2}`;
    },

    // 计算链接标签的Y坐标，根据多条链接进行上下错开
    getLabelPos(d) {
      const y1 = d.source.y;
      const y2 = d.target.y;
      const baseY = (y1 + y2) / 2;

      // 如果只有一条链接，使用基础位置
      if (d.linksCount === 1) {
        return baseY - 8;
      }

      // 根据链接索引计算垂直偏移
      const yOffset = (d.linkIndex - (d.linksCount - 1) / 2) * 18;

      return baseY - 8 + yOffset;
    },

    // 全屏
    fullScreen() {
      const elem = this.$el;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari & Opera */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
      }
      this.isFullScreen = true;
    },
    // 退出全屏
    exitFullScreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        /* Firefox */
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE/Edge */
        document.msExitFullscreen();
      }
      this.isFullScreen = false;
    },
    // 放大
    zoomLarge() {
      this.svg.transition().duration(300).call(this.zoom.scaleBy, 1.2);
    },
    //缩小
    zoomSmall() {
      this.svg.transition().duration(300).call(this.zoom.scaleBy, 0.8);
    },
    // 重置
    resetZoom() {
      const targetNode = this.nodes.find((d) => d.type === "target");
      const offsetX = targetNode ? -targetNode.x : 0;
      const offsetY = targetNode ? -targetNode.y : 0;
      this.svg
        .transition()
        .duration(300)
        .call(
          this.zoom.transform,
          d3.zoomIdentity
            .translate(this.width / 2, this.height / 2)
            .translate(offsetX, offsetY),
        );
    },

    handleResize() {
      this.initGraph();
    },
  },
};
</script>

<style scoped>
.force_graph_container {
  position: relative;
  width: 100%;
  height: 100%;
  height: 650px;
  background-color: #ffffff;
  border-radius: 4px;
  overflow: hidden;
}

.force-graph-svg {
  width: 100%;
  height: 100%;
  cursor: grab;
}

.force-graph-svg:active {
  cursor: grabbing;
}
.level_controls {
  width: auto;
  min-width: 40px;
  height: 46px;
  background: #ffffff;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  position: absolute;
  top: 50px;
  right: 20px;
  z-index: 8;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 8px 4px 10px 4px;
}
.level_label {
  font-size: 12px;
  color: #1a2332;
  line-height: 12px;
  text-align: right;
  font-style: normal;
}
.level_value {
  height: 12px;
  font-size: 12px;
  color: #b44818;
  line-height: 12px;
  text-align: center;
  font-style: normal;
  display: flex;
  align-items: center;
  gap: 6px;
}
.level_value i {
  font-size: 12px;
  transform: rotate(180deg);
  cursor: pointer;
}
.level_value.active i {
  transform: rotate(0deg);
}
.level_controls .btn_control {
  width: 18px;
  font-size: 12px;
  color: #1a2332;
  line-height: 12px;
  text-align: center;
  font-style: normal;
}
.level_controls .btn_control.active {
  color: #b44818;
}
.graph_controls {
  position: absolute;
  top: 100px;
  right: 20px;
  z-index: 8;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 40px;
  padding: 20px 0;
  background: #ffffff;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.btn_control {
  width: 40px;
  height: 20px;
  text-align: center;
  line-height: 20px;
  background-color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 24px;
  font-weight: 500;
  transition: all 0.3s;
}
.btn_control i {
  font-size: 20px;
}

.btn_control:hover {
  background-color: #f0f0f0;
  border-color: #999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn_control:active {
  background-color: #e0e0e0;
}

.legend {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background-color: rgba(255, 255, 255, 0.95);
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 8;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid #999;
}

.node {
  cursor: pointer;
  transition: all 0.2s;
}

.node:hover {
  filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.3));
}

.link {
  pointer-events: none;
  transition: stroke-width 0.2s, opacity 0.2s;
}

.link-label {
  pointer-events: none;
  user-select: none;
  transition: opacity 0.2s, font-weight 0.2s;
  background-color: rgba(255, 255, 255, 0.8);
}

.label {
  pointer-events: none;
  user-select: none;
  transition: opacity 0.2s;
}

.search_tips_box {
  position: absolute;
  top: calc(50% - 50px);
  left: calc(50% + 70px);
  padding: 14px;
  z-index: 8;
  width: auto;
  height: auto;
  background: #ffffff;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.2);
}
.tips_box_header {
  width: 100%;
  height: 1px;
  position: relative;
}
.close_btn {
  position: absolute;
  top: -23px;
  right: -23px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ff6a6a;
  text-align: center;
  line-height: 18px;
  cursor: pointer;
}
.close_btn i {
  color: #fff;
}
.tips_box_content {
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  border: 1px solid #cccccc;
  overflow: hidden;
}
.tips_content_list {
  display: flex;
}
.tips_content_list + .tips_content_list {
  border-top: 1px solid #cccccc;
}
.tips_content_list_label {
  width: 220px;
  font-family: "Arial Bold";
  padding: 8px 10px;
  font-size: 14px;
  color: #1a2332;
  line-height: 16px;
  text-align: left;
  background: #f3f3f3;
}
.tips_content_list_value {
  flex: 1;
  min-width: 80px;
  max-width: 160px;
  padding: 8px 10px;
  font-size: 14px;
  color: #1a2332;
  line-height: 16px;
  text-align: left;
}

.d3_tips {
  position: fixed;
  padding: 14px;
  width: auto;
  height: auto;
  z-index: 8;
  max-width: 328px;
  min-height: 80px;
  background: #ffffff;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
}
.d3_tips_name {
  font-family: "Arial Bold";
  font-weight: normal;
  font-size: 16px;
  color: #1a2332;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  margin-bottom: 10px;
}
.tips_content {
  display: flex;
  gap: 21px;
  align-items: center;
}
.tips_content_item {
  font-size: 12px;
  color: #1a2332;
  line-height: 14px;
  text-align: left;
  font-style: normal;
  position: relative;
  flex-shrink: 0;
}
.tips_content_item:last-child {
  flex-shrink: 1;
}
.tips_content_item + .tips_content_item::before {
  content: "";
  display: inline-block;
  width: 1px;
  height: 12px;
  background: #dddddd;
  position: absolute;
  left: -10px;
  top: calc(50% - 6px);
}
.tips_content_item img {
  width: 34px;
  height: 22px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.2);
  vertical-align: middle;
}
.status_btn {
  width: 62px;
  height: 22px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
  font-style: normal;
  background: #fde2e2;
  color: #f89898;
  border: 1px solid #f89898;
  box-sizing: border-box;
}
.status_btn.active {
  color: #3fb92f;
  background: #f3fff2;
}
@media (max-width: 1920px) {
  .level_controls {
    gap: 4px;
    padding: 6px 4px 8px 4px;
  }
  .level_value i {
    font-size: 10px;
  }
  .level_controls .btn_control {
    width: 16px;
  }
  .graph_controls {
    width: 40px;
    padding: 14px 0;
    gap: 16px;
  }
  .btn_control {
    width: 40px;
    height: 20px;
    line-height: 16px;
    font-size: 20px;
  }

  .search_tips_box {
    padding: 12px;
  }
  .close_btn {
    top: -20px;
    right: -20px;
    width: 16px;
    height: 16px;
    line-height: 16px;
  }
  .close_btn i {
    font-size: 14px;
  }
  .tips_content_list_label {
    width: 190px;
    padding: 6px 8px;
    font-size: 12px;
    line-height: 14px;
  }
  .tips_content_list_value {
    padding: 6px 8px;
    font-size: 12px;
    line-height: 14px;
  }

  .d3_tips {
    padding: 12px;
    max-width: 250px;
    min-height: 65px;
  }
  .d3_tips_name {
    font-size: 14px;
    line-height: 16px;
    margin-bottom: 8px;
  }
  .tips_content {
    gap: 17px;
  }

  .tips_content_item + .tips_content_item::before {
    left: -8px;
  }
  .tips_content_item img {
    width: 26px;
    height: 16px;
  }
  .status_btn {
    width: 46px;
    height: 16px;
    line-height: 15px;
  }
}
</style>
